/**
 * Overflow action menu — a "⋯" trigger and a list of row COMMANDS.
 *
 * SIBLING TO `Dropdown`, NOT BUILT ON IT. Dropdown is a value-select: it has a
 * current value, paints a check on it, and reports a new one. A row's actions
 * have no value and no selected state — picking one fires a handler and the
 * menu closes. Expressing that through Dropdown would mean inventing fake
 * values and throwing them away, so this owns the parts that differ and reuses
 * the parts that don't: the `.dropdown-menu` / `.dropdown-opt` popover
 * vocabulary, the portal-to-body geometry, and the keyboard pattern (focus
 * never leaves the trigger; arrows move an `aria-activedescendant` highlight).
 *
 * Deliberate differences from Dropdown:
 *  - `role="menu"` / `role="menuitem"`, not listbox/option. These are commands.
 *  - RIGHT-aligned to the trigger, because it hangs off a right-aligned
 *    actions cell — a left-aligned menu would hang off the table.
 *  - No type-ahead. A row has three or four actions, not two hundred PCOs.
 *
 * A disabled item stays IN the menu rather than disappearing from it: "Sign
 * out everywhere" greyed with "Not signed in anywhere right now." on hover is
 * an answer; a menu that silently varies in length between rows is a puzzle.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface ActionMenuItem {
  /** Stable identity for the row. */
  key: string;
  label: string;
  /** Fired on click / Enter / Space. The menu is already closed by then. */
  onSelect: () => void;
  disabled?: boolean;
  /** Destructive command — danger ink, danger-tinted highlight. */
  danger?: boolean;
  /** Native tooltip. Where a disabled item says WHY it is disabled. */
  title?: string;
}

/** Gap between trigger and menu, and the viewport margin the menu keeps. */
const GAP = 4;
const EDGE = 8;
const MAX_H = 320;
const MAX_W = 320;
const MIN_W = 190;
/** Floor for the menu box so a cramped side scrolls rather than slivers. */
const MIN_H = 120;

interface MenuPos {
  left: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

const samePos = (a: MenuPos, b: MenuPos) =>
  a.left === b.left && a.top === b.top && a.bottom === b.bottom && a.maxHeight === b.maxHeight;

let actionMenuSeq = 0;

export function ActionMenu({
  items,
  label,
  disabled,
  className,
}: {
  items: ReadonlyArray<ActionMenuItem>;
  /** Accessible name. Carry the row identity — "Actions for Josh Gardner". */
  label: string;
  disabled?: boolean;
  /** Extra classes on the trigger button. */
  className?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const idRef = useRef<string>();
  if (idRef.current === undefined) idRef.current = `action-menu-${++actionMenuSeq}`;
  const id = idRef.current;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<MenuPos>({ left: 0, maxHeight: MAX_H });

  /* ── positioning ──────────────────────────────────────────────────────── */

  // One function for open, scroll, and resize. On the first pass the menu is
  // not in the DOM yet, so it places BELOW off the trigger's own box; the
  // layout effect runs it again with a real measurement and flips up only if
  // the real box would clip.
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const menu = menuRef.current;
    const spaceBelow = window.innerHeight - r.bottom - GAP - EDGE;
    const spaceAbove = r.top - GAP - EDGE;
    const wanted = menu ? Math.min(menu.scrollHeight, MAX_H) : 0;
    const up = wanted > spaceBelow && spaceAbove > spaceBelow;
    const maxHeight = Math.max(MIN_H, Math.min(MAX_H, up ? spaceAbove : spaceBelow));
    const w = menu ? menu.getBoundingClientRect().width : MIN_W;
    // Right edges meet — the trigger sits at the right end of its cell.
    const left = Math.max(EDGE, Math.min(r.right - w, window.innerWidth - EDGE - w));
    const next: MenuPos = {
      left,
      maxHeight,
      top: up ? undefined : r.bottom + GAP,
      bottom: up ? window.innerHeight - r.top + GAP : undefined,
    };
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, []);

  // Measure-then-correct, before paint — the clipped frame is never shown.
  useLayoutEffect(() => {
    if (!open) return;
    place();
  });

  useEffect(() => {
    if (!open) return;
    // Capture phase: settings panes scroll internally, and a bubbling scroll
    // listener on window never hears those.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  /* ── open / close / fire ──────────────────────────────────────────────── */

  const firstEnabled = (from: number, step: number) => {
    for (let i = from; i >= 0 && i < items.length; i += step) {
      if (!items[i]?.disabled) return i;
    }
    return -1;
  };

  const openMenu = () => {
    if (disabled || items.length === 0) return;
    setActive(firstEnabled(0, 1));
    place();
    setOpen(true);
  };

  const closeMenu = (refocus = true) => {
    setOpen(false);
    setActive(-1);
    if (refocus) triggerRef.current?.focus();
  };

  const fire = (index: number) => {
    const item = items[index];
    if (!item || item.disabled) return;
    // Close FIRST: most of these open a confirm dialog, and that dialog should
    // never come up behind a menu that is still on screen.
    closeMenu();
    item.onSelect();
  };

  // Highlight follows the keyboard.
  useEffect(() => {
    if (!open || active < 0) return;
    menuRef.current?.querySelector(`#${id}-item-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);

  // Outside click. mousedown (not click) so the menu is gone before the click
  // lands on whatever was underneath it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      // No refocus — the user is deliberately going somewhere else.
      closeMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  /* ── keyboard ─────────────────────────────────────────────────────────── */

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        return;
      case 'Tab':
        // Not prevented — Tab still moves focus, it just doesn't leave a menu
        // hanging over the page.
        closeMenu(false);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        fire(active);
        return;
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => {
          const next = firstEnabled(i + 1, 1);
          return next < 0 ? i : next;
        });
        return;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => {
          const next = firstEnabled(i - 1, -1);
          return next < 0 ? i : next;
        });
        return;
      case 'Home':
        e.preventDefault();
        setActive(firstEnabled(0, 1));
        return;
      case 'End':
        e.preventDefault();
        setActive(firstEnabled(items.length - 1, -1));
        return;
      default:
        return;
    }
  };

  /* ── render ───────────────────────────────────────────────────────────── */

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={['icon-btn', 'action-menu-trigger', className].filter(Boolean).join(' ')}
        disabled={disabled || items.length === 0}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${id}-menu` : undefined}
        aria-activedescendant={open && active >= 0 ? `${id}-item-${active}` : undefined}
        aria-label={label}
        title={label}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <MoreHorizontal size={16} aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={`${id}-menu`}
            role="menu"
            aria-label={label}
            className="dropdown-menu"
            style={{
              position: 'fixed',
              left: pos.left,
              ...(pos.top !== undefined ? { top: pos.top } : {}),
              ...(pos.bottom !== undefined ? { bottom: pos.bottom } : {}),
              minWidth: MIN_W,
              maxWidth: Math.min(MAX_W, window.innerWidth - EDGE * 2),
              maxHeight: pos.maxHeight,
            }}
          >
            {items.map((item, i) => (
              <div
                key={item.key}
                id={`${id}-item-${i}`}
                role="menuitem"
                aria-disabled={item.disabled || undefined}
                title={item.title}
                className={[
                  'dropdown-opt',
                  item.danger ? 'dropdown-opt--danger' : null,
                  i === active ? 'is-active' : null,
                  item.disabled ? 'is-disabled' : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                // Pointer and keyboard share ONE highlight, so the mouse never
                // leaves a second, competing marker behind.
                onMouseEnter={() => !item.disabled && setActive(i)}
                onClick={() => fire(i)}
              >
                <span className="dropdown-opt-label">{item.label}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
